import React, { useRef, useState } from "react";
import { Field, Panel } from '../../../../layouts';
import { confirm } from 'devextreme/ui/dialog';
import { Box, Item } from "devextreme-react/box";
import SelectBox from "devextreme-react/select-box";
import DateBox from "devextreme-react/date-box";
import { Item as ToolbarItem, Toolbar } from "devextreme-react/toolbar";
import Button from "devextreme-react/button";
import { useGetLayouts } from "../api/get-layouts";
import { useAuth } from "../../../../contexts/auth";
import { useGetCustomReportData } from "../api/get-custom-report-data";
import DataGrid from "devextreme-react/data-grid";
import { Workbook } from "exceljs";
import { exportDataGrid } from "devextreme/excel_exporter";
import { saveAs } from "file-saver";
import { Popup, ToolbarItem as PopupToolbarItem } from "devextreme-react/popup";
import { LoadPopupPanel } from "../../../../components";
import { useGetCustomReportColumns } from "../api/get-custom-report-columns";
import List, { ItemDragging } from 'devextreme-react/list';
import { CustomReportColumnType } from "../types/CustomReportColumnType";
import TextBox from "devextreme-react/text-box";
import { useDeleteLayout } from "../api/delete-layout";
import { showErrorMessage } from "../../../../utils/messages";
import { useSaveLayout } from "../api/save-layout";
import './CustomReportForm.scss'

export default function CustomReportForm() {
    const { user } = useAuth();
    const [reportType, setReportType] = useState<string>("PO");
    const [layoutCode, setLayoutCode] = useState<string>("");
    const [editingLayoutCode, setEditingLayoutCode] = useState<string>("");
    const [editingColumns, setEditingColumns] = useState<CustomReportColumnType[]>([]);
    const savedLayouts = useGetLayouts(user?.companyId, reportType);
    const customReportColumns = useGetCustomReportColumns(user?.companyId, reportType, layoutCode);
    const defaultCustomReportColumns = useGetCustomReportColumns(user?.companyId, reportType, '');
    const [dateFrom, setDateFrom] = useState<Date | undefined>();
    const [dateTo, setDateTo] = useState<Date | undefined>();
    const reportData = useGetCustomReportData(user?.companyId, reportType, layoutCode, dateFrom, dateTo);
    const [exportToExcel, setExportToExcel] = useState<boolean>(false);
    const [popupVisible, setPopupVisible] = useState<boolean>(false);
    const [selectedItemKeys, setSelectedItemKeys] = useState<CustomReportColumnType[]>([]);
    const list = useRef<List>(null);
    const deleteLayout = useDeleteLayout(
        { reportType: reportType, layoutName: layoutCode },
        result => {
            savedLayouts.refetch();
            setPopupVisible(false);
        }, error => {
            showErrorMessage(error);
        }
    )

    const saveLayoutCommand = useSaveLayout(
        { reportType: reportType, layoutName: editingLayoutCode, columns: editingColumns },
        result => {
            savedLayouts.refetch().then(() => {
                setLayoutCode(editingLayoutCode);
            });
            setPopupVisible(false);
        }, error => {
            showErrorMessage(error);
        }
    )


    return (
        <>
            <Panel header="Create or View Custom Reports" isLoading={savedLayouts.isLoading} className="custom-reports">
                <Box>
                    <Item ratio={1}>
                    </Item>
                    <Item baseSize={400}>
                        <Field label='Report Type'>
                            <SelectBox
                                value={reportType}
                                dataSource={[{ name: "Purchase Order", value: "PO" }]}
                                displayExpr="name"
                                valueExpr="value"
                            >
                            </SelectBox>
                        </Field>
                        <Field label='Saved Layouts'>
                            <SelectBox
                                value={layoutCode}
                                onValueChanged={(e) => setLayoutCode(e.value)}
                                dataSource={savedLayouts?.data ?? []}
                            >
                            </SelectBox>
                        </Field>
                        <Field label='Date From'>
                            <DateBox
                                showClearButton={true}
                                value={dateFrom}
                                onValueChanged={(e) => setDateFrom(e.value)}
                            >
                            </DateBox>
                        </Field>
                        <Field label='Date To'>
                            <DateBox
                                showClearButton={true}
                                value={dateTo}
                                onValueChanged={(e) => setDateTo(e.value)}
                            >
                            </DateBox>
                        </Field>
                    </Item>
                    <Item ratio={1}>
                    </Item>
                </Box>
                <Toolbar className='button-toolbar'>
                    <ToolbarItem location='center'>
                        <Button
                            text="Select Columns"
                            disabled={exportToExcel}
                            onClick={(e) => {
                                setEditingLayoutCode(layoutCode);
                                setPopupVisible(true);
                                customReportColumns.refetch().then((res) => {
                                    if (res.data) {
                                        setEditingColumns(res.data);
                                        setSelectedItemKeys(res.data.filter(x => x.included === true));
                                    }
                                });
                            }}>
                        </Button>
                    </ToolbarItem>
                    <ToolbarItem location='center'>
                        <Button
                            text="Export Report"
                            disabled={exportToExcel}
                            onClick={(e) => {
                                setExportToExcel(true);
                                reportData.refetch();
                            }}>
                        </Button>
                    </ToolbarItem>
                </Toolbar>
            </Panel>
            <DataGrid
                columns={reportData.data?.columns ?? []}
                dataSource={reportData.data?.rows}
                visible={false}
                onContentReady={(e) => {
                    if (exportToExcel) {
                        const workbook = new Workbook();
                        const worksheet = workbook.addWorksheet("Sheet1");
                        const fileName = 'PurchaseOrdersDataExport.xlsx';

                        exportDataGrid({
                            component: e.component,
                            worksheet,
                            keepColumnWidths: false
                        }).then(() => {
                            worksheet.columns.forEach(column => {
                                const lengths = column.values?.map(v => v?.toString().length ?? 0 + 10) ?? [];
                                column.width = Math.max(Math.max(...lengths.filter(v => typeof v === 'number')) + 2, ((column?.header ?? "").length) + 6)
                            });
                            workbook.xlsx.writeBuffer().then((buffer) => {
                                saveAs(new Blob([buffer], { type: 'application/octet-stream' }), fileName);
                            });
                            setExportToExcel(false);
                        });
                    }
                }}
            ></DataGrid>
            <Popup
                title={"Purchase Order Data Export Columns"}
                visible={popupVisible}
                hideOnOutsideClick={true}
                onHiding={() => setPopupVisible(false)}
                width={500}
                showCloseButton={true}
            >
                <List
                    items={editingColumns}
                    ref={list}
                    showSelectionControls={true}
                    selectionMode={'multiple'}
                    selectByClick={false}
                    selectedItemKeys={selectedItemKeys}
                    itemKeyFn={x => x.fieldName}
                    onSelectedItemKeysChange={(e) => {
                        setSelectedItemKeys(e);
                    }}
                    displayExpr={'caption'}
                    height={'auto'}
                    className={'custom-report-form'}
                >
                    <ItemDragging allowReordering={true} />
                </List>
                <Field label='Layout name'>
                    <TextBox value={editingLayoutCode} onValueChange={value => setEditingLayoutCode(value)}></TextBox>
                </Field>
                <PopupToolbarItem toolbar='bottom' location='before' visible={layoutCode !== ''}>
                    <Button
                        text="Delete"
                        onClick={() => {
                            let result = confirm('Are you sure you want to delete ' + layoutCode + ' layout?', '');
                            result.then((dialogResult) => {
                                if (!dialogResult)
                                    return;

                                deleteLayout.mutate();
                            });
                        }}>
                    </Button>
                </PopupToolbarItem>
                <PopupToolbarItem toolbar='bottom' location='center'>
                    <Button
                        text="Restore Defaults"
                        onClick={() => {
                            defaultCustomReportColumns.refetch().then(res => {
                                if (res.data) {
                                    setEditingColumns(res.data);
                                    setSelectedItemKeys(res.data.filter(x => x.included === true));
                                }
                            });
                        }}>
                    </Button>
                </PopupToolbarItem>
                <PopupToolbarItem toolbar='bottom' location='after'>
                    <Button
                        text="OK"
                        onClick={() => {
                            let columns = [...editingColumns];
                            for (let i = 0; i < columns.length; i++) {
                                columns[i].sortOrder = i + 1;
                                if (selectedItemKeys.some(x => x.included && x.fieldName === columns[i].fieldName)) {
                                    columns[i].included = true;
                                } else {
                                    columns[i].included = false;
                                }
                            }
                            setEditingColumns(columns);
                            saveLayoutCommand.mutate();
                        }}>
                    </Button>
                </PopupToolbarItem>
                <PopupToolbarItem toolbar='bottom' location='after'>
                    <Button
                        text="Cancel"
                        onClick={() => setPopupVisible(false)}>
                    </Button>
                </PopupToolbarItem>
                <LoadPopupPanel
                    visible={customReportColumns.isLoading || defaultCustomReportColumns.isLoading}></LoadPopupPanel>
            </Popup>
        </>
    )
}
